import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { showingbookerAPI } from '../../../services/showingbookerAPI';

const Container = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
  background: #f8f9fa;
`;

const Title = styled.h3`
  margin: 0;
  color: #333;
  font-size: 1.3rem;
  font-weight: 600;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  font-size: 14px;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
  }
`;

const FilterInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
  }
`;

const ExportContainer = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  border: 1px solid ${props => props.variant === 'sync' ? '#28a745' : '#007AFF'};
  background: ${props => props.variant === 'sync' ? '#28a745' : '#007AFF'};
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 5px;
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 13px;
  }
  
  &:hover {
    background: ${props => props.variant === 'sync' ? '#218838' : '#0056b3'};
    border-color: ${props => props.variant === 'sync' ? '#1e7e34' : '#004494'};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ExportButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #007AFF;
  background: ${props => props.primary ? '#007AFF' : 'white'};
  color: ${props => props.primary ? 'white' : '#007AFF'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.primary ? '#0056b3' : '#f0f8ff'};
  }
`;

const TableContainer = styled.div`
  max-height: 600px;
  overflow-y: auto;
  border-top: 1px solid #eee;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const TableHeaderCell = styled.th`
  padding: 15px 12px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #ddd;
  font-size: 14px;
  cursor: pointer;
  user-select: none;
  
  &:hover {
    background: #e9ecef;
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  &:hover {
    background: #f8f9fa;
  }
  
  &:nth-child(even) {
    background: #fafafa;
  }
  
  &:nth-child(even):hover {
    background: #f0f0f0;
  }
`;

const TableCell = styled.td`
  padding: 12px;
  border-bottom: 1px solid #eee;
  font-size: 14px;
  vertical-align: top;
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    switch(props.status) {
      case 'confirmed': return '#d4edda';
      case 'pending': return '#fff3cd';
      case 'cancelled': return '#f8d7da';
      default: return '#e2e3e5';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'confirmed': return '#155724';
      case 'pending': return '#856404';
      case 'cancelled': return '#721c24';
      default: return '#6c757d';
    }
  }};
`;

const ContactButton = styled.button`
  padding: 4px 8px;
  border: 1px solid #007AFF;
  background: white;
  color: #007AFF;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #007AFF;
    color: white;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
`;

const UpcomingShowings = ({ properties }) => {
  const [showings, setShowings] = useState([]);
  const [filteredShowings, setFilteredShowings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filters, setFilters] = useState({
    property: '',
    resident: '',
    owner: '',
    dateFrom: '',
    dateTo: ''
  });


  useEffect(() => {
    loadUpcomingShowings();
  }, [properties]);

  const loadUpcomingShowings = async () => {
    try {
      setLoading(true);
      const upcomingShowings = await showingbookerAPI.getUpcomingShowings();
      
      // Transform API data to match component expectations
      const transformedShowings = upcomingShowings.map(showing => {
        // Create datetime for proper sorting
        const dateStr = showing.showing_date.split('T')[0]; // Get YYYY-MM-DD
        const timeStr = showing.showing_time; // Get HH:MM:SS
        const datetime = new Date(`${dateStr}T${timeStr}`);
        
        return {
          id: showing.id,
          propertyId: showing.property_id,
          propertyAddress: showing.property_address,
          date: dateStr,
          time: timeStr,
          datetime: datetime,
          buyerName: showing.buyer_name,
          buyerEmail: showing.buyer_email,
          buyerPhone: showing.buyer_phone,
          status: showing.status,
          notes: showing.notes,
          createdAt: showing.created_at
        };
      });
      
      // Sort by datetime (nearest upcoming first) by default
      transformedShowings.sort((a, b) => a.datetime - b.datetime);
      
      setShowings(transformedShowings);
    } catch (error) {
      console.error('Error loading upcoming showings:', error);
      setShowings([]);
    } finally {
      setLoading(false);
    }
  };

  const syncFromBookings = async () => {
    try {
      setSyncing(true);
      console.log('🔄 Syncing upcoming showings from bookings...');
      
      // Refresh the showings data by reloading from API
      await loadUpcomingShowings();
      
      console.log('✅ Successfully synced upcoming showings');
    } catch (error) {
      console.error('❌ Error syncing upcoming showings:', error);
    } finally {
      setSyncing(false);
    }
  };

  // Apply filters
  useEffect(() => {
    let filtered = [...showings];
    
    if (filters.property) {
      filtered = filtered.filter(showing => 
        showing.propertyAddress.toLowerCase().includes(filters.property.toLowerCase())
      );
    }
    
    if (filters.resident) {
      filtered = filtered.filter(showing => 
        showing.residentName.toLowerCase().includes(filters.resident.toLowerCase())
      );
    }
    
    if (filters.owner) {
      filtered = filtered.filter(showing => 
        showing.ownerName.toLowerCase().includes(filters.owner.toLowerCase())
      );
    }
    
    if (filters.dateFrom) {
      filtered = filtered.filter(showing => showing.date >= filters.dateFrom);
    }
    
    if (filters.dateTo) {
      filtered = filtered.filter(showing => showing.date <= filters.dateTo);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (sortField === 'date' || sortField === 'datetime') {
        aValue = a.datetime;
        bValue = b.datetime;
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    setFilteredShowings(filtered);
  }, [showings, filters, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Time', 'Property Address', 'Buyer Name', 'Buyer Contact', 'Agent', 'Status', 'Owner', 'Resident'];
    const csvData = [
      headers,
      ...filteredShowings.map(showing => [
        showing.date,
        showing.time,
        showing.propertyAddress,
        showing.buyerName,
        `${showing.buyerEmail} | ${showing.buyerPhone}`,
        showing.agentName || 'Direct Buyer',
        showing.status,
        showing.ownerName,
        showing.residentName
      ])
    ];
    
    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `upcoming-showings-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportToCalendar = () => {
    let icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//ShowingBooker//EN'
    ];
    
    filteredShowings.forEach(showing => {
      const start = new Date(showing.datetime);
      const end = new Date(start.getTime() + 30 * 60000); // 30 minutes
      
      icsContent.push(
        'BEGIN:VEVENT',
        `DTSTART:${start.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
        `DTEND:${end.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
        `SUMMARY:Property Showing - ${showing.propertyAddress}`,
        `DESCRIPTION:Buyer: ${showing.buyerName}\\nAgent: ${showing.agentName || 'Direct'}\\nContact: ${showing.buyerPhone}\\nStatus: ${showing.status}`,
        `LOCATION:${showing.propertyAddress}`,
        `UID:${showing.id}@showingbooker.com`,
        'END:VEVENT'
      );
    });
    
    icsContent.push('END:VCALENDAR');
    
    const blob = new Blob([icsContent.join('\r\n')], { type: 'text/calendar' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `upcoming-showings-${new Date().toISOString().split('T')[0]}.ics`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const printSchedule = () => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <html>
        <head>
          <title>Upcoming Showings Schedule</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; border-bottom: 2px solid #007AFF; padding-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { padding: 8px; border: 1px solid #ddd; text-align: left; }
            th { background: #f8f9fa; font-weight: 600; }
            .status { padding: 2px 6px; border-radius: 4px; font-size: 12px; }
            .confirmed { background: #d4edda; color: #155724; }
            .pending { background: #fff3cd; color: #856404; }
            .cancelled { background: #f8d7da; color: #721c24; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <h1>📅 Upcoming Showings Schedule</h1>
          <p>Generated on: ${new Date().toLocaleDateString()}</p>
          <p>Total Showings: ${filteredShowings.length}</p>
          <table>
            <thead>
              <tr>
                <th>Date & Time</th>
                <th>Property</th>
                <th>Buyer</th>
                <th>Agent</th>
                <th>Status</th>
                <th>Contact</th>
              </tr>
            </thead>
            <tbody>
              ${filteredShowings.map(showing => `
                <tr>
                  <td>${showing.date} ${showing.time}</td>
                  <td>${showing.propertyAddress}</td>
                  <td>${showing.buyerName}</td>
                  <td>${showing.agentName || 'Direct'}</td>
                  <td><span class="status ${showing.status}">${showing.status}</span></td>
                  <td>${showing.buyerPhone}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const contactBuyer = (showing) => {
    const message = `Hi ${showing.buyerName}, this is regarding your upcoming showing at ${showing.propertyAddress} on ${showing.date} at ${showing.time}.`;
    window.open(`sms:${showing.buyerPhone.replace(/\D/g, '')}?body=${encodeURIComponent(message)}`);
  };

  if (loading) {
    return (
      <Container>
        <LoadingState>
          <div>📅 Loading upcoming showings...</div>
        </LoadingState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <div>
          <Title>📅 Upcoming Showings</Title>
          <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
            Next 14 days • {filteredShowings.length} showings
          </div>
        </div>
        
        <ExportContainer>
          <ActionButton
            variant="sync"
            onClick={syncFromBookings}
            disabled={syncing || loading}
            title="Refresh upcoming showings from bookings database"
          >
            {syncing ? '⏳ Syncing...' : '🔄 Sync from Bookings'}
          </ActionButton>
          <ExportButton onClick={exportToCSV}>📄 CSV</ExportButton>
          <ExportButton onClick={exportToCalendar}>📅 Calendar</ExportButton>
          <ExportButton onClick={printSchedule} primary>🖨️ Print</ExportButton>
        </ExportContainer>
      </Header>

      <div style={{ padding: '20px', borderBottom: '1px solid #eee', background: '#fafafa' }}>
        <FilterContainer>
          <FilterInput
            type="text"
            placeholder="Filter by property..."
            value={filters.property}
            onChange={(e) => handleFilterChange('property', e.target.value)}
          />
          <FilterInput
            type="text"
            placeholder="Filter by resident..."
            value={filters.resident}
            onChange={(e) => handleFilterChange('resident', e.target.value)}
          />
          <FilterInput
            type="text"
            placeholder="Filter by owner..."
            value={filters.owner}
            onChange={(e) => handleFilterChange('owner', e.target.value)}
          />
          <FilterInput
            type="date"
            placeholder="From date"
            value={filters.dateFrom}
            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
          />
          <FilterInput
            type="date"
            placeholder="To date"
            value={filters.dateTo}
            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
          />
        </FilterContainer>
      </div>

      <TableContainer>
        {filteredShowings.length > 0 ? (
          <Table>
            <TableHeader>
              <tr>
                <TableHeaderCell onClick={() => handleSort('date')}>
                  Date & Time {sortField === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => handleSort('propertyAddress')}>
                  Property {sortField === 'propertyAddress' && (sortDirection === 'asc' ? '↑' : '↓')}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => handleSort('buyerName')}>
                  Buyer {sortField === 'buyerName' && (sortDirection === 'asc' ? '↑' : '↓')}
                </TableHeaderCell>
                <TableHeaderCell>Agent</TableHeaderCell>
                <TableHeaderCell onClick={() => handleSort('status')}>
                  Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                </TableHeaderCell>
                <TableHeaderCell>Owner/Resident</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {filteredShowings.map(showing => (
                <TableRow key={showing.id}>
                  <TableCell>
                    <div style={{ fontWeight: '500' }}>{showing.date}</div>
                    <div style={{ fontSize: '13px', color: '#666' }}>{showing.time}</div>
                  </TableCell>
                  <TableCell>
                    <div style={{ fontWeight: '500' }}>{showing.propertyAddress}</div>
                    {showing.specialRequests && (
                      <div style={{ fontSize: '12px', color: '#856404', marginTop: '2px' }}>
                        📝 {showing.specialRequests}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div style={{ fontWeight: '500' }}>{showing.buyerName}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{showing.buyerEmail}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{showing.buyerPhone}</div>
                  </TableCell>
                  <TableCell>
                    {showing.agentName ? (
                      <div>
                        <div style={{ fontWeight: '500' }}>{showing.agentName}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{showing.agentEmail}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{showing.agentPhone}</div>
                      </div>
                    ) : (
                      <span style={{ color: '#666' }}>Direct Buyer</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={showing.status}>
                      {showing.status.charAt(0).toUpperCase() + showing.status.slice(1)}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                    <div style={{ fontSize: '13px' }}>
                      <div><strong>Owner:</strong> {showing.ownerName}</div>
                      <div><strong>Resident:</strong> {showing.residentName}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <ContactButton onClick={() => contactBuyer(showing)}>
                      📱 Text
                    </ContactButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <EmptyState>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
            <h3>No upcoming showings</h3>
            <p>Showings will appear here once they are booked for your properties.</p>
          </EmptyState>
        )}
      </TableContainer>
    </Container>
  );
};

export default UpcomingShowings;